import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { ProfileExtrusionDescriptor } from "building-types/profiles/profileTypes";
import type { BaseDescriptor } from "building-types/views/baseDescriptorTypes";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import type { ViewNotesSettingsTable, EntityAnnotationSetting } from "building-types/views/viewEntityNotes";
import { ViewEntityType } from "building-types/views/viewEntityType";
import type {
  ViewEntityDescriptor,
  EntityDescriptor,
  ExtrusionViewEntity,
  BRepViewEntity,
} from "building-types/views/viewEntityTypes";
import type { ViewNote } from "building-types/views/viewNotes";
import uid from "common/utils/uid";
import type { Extrusion, BRep, Face } from "geom/brep";
import type { UVTransformation } from "geom/uv";

export function baseDescriptorNew(
  viewEntityType: ViewEntityType = ViewEntityType.NONE,
  entityId: string = uid(),
): BaseDescriptor {
  return { entityId, entityType: viewEntityType } as BaseDescriptor;
}

export function viewEntityDescriptorNew(
  entityId: string,
  entityType: ViewEntityType,
  viewNote: ViewNote | undefined,
): ViewEntityDescriptor {
  return {
    type: "VIEW_ENTITY_DESCRIPTOR",
    entityId,
    entityType,
    viewNote,
  };
}

export function getDescriptorEntityAnnotationSetting(
  descriptor: EntityDescriptor,
  settings: ViewNotesSettingsTable,
): { requirements: EntityAnnotationSetting | undefined; bypass: boolean } {
  if (!descriptor) {
    return { requirements: { enabled: false }, bypass: false };
  }

  const d = descriptor as ViewEntityDescriptor;
  if (d.type === "VIEW_ENTITY_DESCRIPTOR") {
    return {
      requirements: {
        enabled: !!d.viewNote?.enable,
        tagOnlyOne: d.viewNote?.tagOnlyOne,
        text: d.viewNote?.text,
      },
      bypass: true,
    };
  }

  switch ((descriptor as BaseDescriptor | ProfileExtrusionDescriptor).entityType) {
    case ViewEntityType.PROFILE_EXTRUSION: {
      const resolvedDescriptor = descriptor as ProfileExtrusionDescriptor;
      return {
        requirements: {
          enabled: true,
          text: resolvedDescriptor?.elevationNote ?? "",
          tagOnlyOne: true,
        },
        bypass: false,
      };
    }
    default: {
      return { requirements: settings[descriptor.entityType] ?? { enabled: false }, bypass: false };
    }
  }
}

export function extrusionViewEntityNew(
  extrusion: Extrusion,
  entityHandleForSelectingReportingUnit: DocEntityHandle | undefined,
  descriptor?: EntityDescriptor,
  material?: ExteriorMaterial,
  pickHandle?: DocEntityHandle,
  materialTransformation?: UVTransformation,
): ExtrusionViewEntity {
  return {
    entityId: uid(),
    entityHandleForSelectingReportingUnit,
    extrusion,
    descriptor: { entityType: ViewEntityType.NONE, ...descriptor },
    material: material,
    materialTransformation,
    pickHandle,
  };
}

export function brepViewEntityNew(
  brep: BRep,
  entityHandleForSelectingReportingUnit: DocEntityHandle | undefined,
  descriptor?: EntityDescriptor,
  material?: ExteriorMaterial,
  pickHandle?: DocEntityHandle,
  materialTransformation?: UVTransformation,
): BRepViewEntity {
  return {
    entityId: uid(),
    entityHandleForSelectingReportingUnit,
    brep,
    descriptor: { entityType: ViewEntityType.NONE, ...descriptor },
    material: material,
    materialTransformation,
    pickHandle,
  };
}

export function faceArrayViewEntityNew(
  faces: Face[],
  descriptor?: EntityDescriptor,
  material?: ExteriorMaterial,
  pickHandle?: DocEntityHandle,
  materialTransformation?: UVTransformation,
  entityId?: string,
  entityHandleForSelectingReportingUnit?: DocEntityHandle,
): FaceArrayViewEntity {
  if (!entityId) {
    entityId = uid();
  }
  return {
    entityId,
    entityHandleForSelectingReportingUnit,
    faces,
    descriptor: { entityType: ViewEntityType.NONE, ...descriptor },
    material,
    materialTransformation,
    pickHandle,
  };
}
